'use client'
import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { Navbar, Typography } from '@material-tailwind/react'
import Image from 'next/image'
import { TopSubnav } from '../TopSubnav'
import { HamburgerMenu } from '../HamburgerMenu'
import { getMaterialTheme } from './theme'
import { resolveHref } from '@/utilities/resolveHref'
import type { Header } from '@/payload-types'
import { NavCTA } from '@/components/ui/nav-cta'

type NavigationBackground = 'lime' | 'black' | 'taupe' | 'white' | 'beige'
type NavItem = NonNullable<Header['primaryNavigation']>[number]
type MegaMenuItem = NonNullable<NonNullable<NavItem['megaMenu']>['items']>[number]

interface MaterialNavbarProps {
  navigationBackground?: NavigationBackground
  data: Header
}

export const MaterialNavbar: React.FC<MaterialNavbarProps> = ({ navigationBackground = 'white', data }) => {

  // Mobile navigation state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})

  // Desktop mega menu state
  const [openMegaMenu, setOpenMegaMenu] = useState<string | null>(null)

  // Logo context menu state
  const [logoContextMenuOpen, setLogoContextMenuOpen] = useState(false)
  const logoContextMenuRef = useRef<HTMLDivElement>(null)
  const logoRef = useRef<HTMLAnchorElement>(null)

  const pathname = usePathname()

  // Get active link color based on theme (neutral colors for cleaner nav)
  const getActiveColor = (): string => {
    const isDarkBackground = navigationBackground === 'black'
    return isDarkBackground ? 'text-white' : 'text-black'
  }

  // Get hex color value for CSS variables (hover states)
  const getHoverColorValue = (): string => {
    const isDarkBackground = navigationBackground === 'black'
    return isDarkBackground ? '#FFFFFF' : '#000000'
  }

  // Helper function to check if a nav item or any of its sub-items are active
  const isNavItemActive = (navItem: NavItem): boolean => {
    // Check if the main nav link matches (only if href is non-empty)
    const mainHref = resolveHref(navItem.navigationLink, '')
    if (mainHref && pathname === mainHref) return true

    // Check if any mega menu sub-item matches (only if href is non-empty)
    if (navItem.megaMenu?.enabled && navItem.megaMenu.items) {
      return navItem.megaMenu.items.some((subItem: MegaMenuItem) => {
        const subHref = resolveHref(subItem.itemLink, '')
        return subHref && pathname === subHref
      })
    }

    return false
  }

  // Reset mobile menu and mega menu state on route change
  useEffect(() => {
    setIsMobileMenuOpen(false)
    setExpandedSections({})
    setOpenMegaMenu(null)
  }, [pathname])

  // Mobile navigation functions
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
    setExpandedSections({})
  }

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  // Body scroll lock when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  // Close menu with Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false)
        }
        if (openMegaMenu) {
          setOpenMegaMenu(null)
        }
      }
    }

    if (isMobileMenuOpen || openMegaMenu) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isMobileMenuOpen, openMegaMenu])

  // Close mega menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openMegaMenu) {
        const target = e.target as HTMLElement
        if (!target.closest('[data-mega-menu]') && !target.closest('[data-mega-menu-trigger]')) {
          setOpenMegaMenu(null)
        }
      }
    }

    if (openMegaMenu) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [openMegaMenu])

  // Logo context menu handlers
  const handleLogoContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setLogoContextMenuOpen(true)
  }

  // Close logo context menu when clicking outside, pressing Escape, or scrolling
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        logoContextMenuRef.current &&
        !logoContextMenuRef.current.contains(e.target as Node) &&
        logoRef.current &&
        !logoRef.current.contains(e.target as Node)
      ) {
        setLogoContextMenuOpen(false)
      }
    }

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setLogoContextMenuOpen(false)
      }
    }

    const handleScroll = () => {
      setLogoContextMenuOpen(false)
    }

    if (logoContextMenuOpen) {
      document.addEventListener('click', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
      window.addEventListener('scroll', handleScroll, true)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [logoContextMenuOpen])

  // Get theme based on navigationBackground prop
  const theme = getMaterialTheme(navigationBackground)

  return (
    <>
      <header className="relative">
        {/* Top Subnav Bar */}
        <TopSubnav data={data.topSubnav} />

        {/* Main Navbar */}
        <Navbar className={`${theme.mainNav} border-none shadow-none rounded-none p-0`}>
          <div className="px-7 pt-5 lg:pt-[26px] md:px-9 xl:px-16 max-w-[1440px] mx-auto">
            {/* Main Navigation Container */}
            <div className="flex items-center justify-between pb-5 lg:pb-[26px]">
              {/* Logo + Nav Group - desktop only */}
              <div className="flex items-center gap-0">
                {/* Logo - visible on all sizes */}
                <div className="flex items-center">
                  <Link href="/" ref={logoRef} onContextMenu={handleLogoContextMenu}>
                    <Image
                      src={`/assets-rebrand/logos/civo-${theme.logoVariant}.svg`}
                      alt="Civo"
                      width={88}
                      height={36}
                      priority
                    />
                  </Link>
                  {/* Vertical separator bar - desktop only */}
                  <div className={`hidden lg:block w-px h-8 lg:ml-8 ${theme.textColor.includes('white') ? 'bg-white' : 'bg-black'}`}></div>
                </div>

                {/* Navigation Links - desktop only */}
                <nav className="hidden lg:flex lg:flex-row lg:items-center gap-5 lg:pl-5">
                  {(data.primaryNavigation || []).map((navItem, index) => {
                  const isActive = isNavItemActive(navItem)

                  if (navItem.megaMenu?.enabled && navItem.megaMenu.items) {
                    // Render navigation item with mega menu
                    const menuId = navItem.id || `nav-${index}`
                    const isOpen = openMegaMenu === menuId
                    const navLabel = navItem.megaMenu.dropDownLabel || navItem.navigationLink?.label

                    return (
                      <div key={menuId}>
                        <button
                          data-mega-menu-trigger
                          onClick={() => setOpenMegaMenu(isOpen ? null : menuId)}
                          className={`flex items-center gap-1.5 ${isActive ? getActiveColor() : theme.textColor}`}
                        >
                          <Typography className={`nav-copy ${isOpen || isActive ? 'lg:underline' : ''}`}>
                            {navLabel}
                          </Typography>
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 17 17"
                            fill="none"
                            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                          >
                            <path
                              d="M4 6L8.5 10.5L13 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    )
                  } else {
                    // Render simple navigation item without mega menu
                    return (
                      <div key={navItem.id || index}>
                        <Link href={resolveHref(navItem.navigationLink, '/')} target={navItem.navigationLink?.newTab ? '_blank' : undefined} rel={navItem.navigationLink?.newTab ? 'noopener noreferrer' : undefined} className={isActive ? getActiveColor() : theme.textColor}>
                          <Typography className={`nav-copy ${isActive ? 'lg:underline' : ''}`}>
                            {navItem.navigationLink?.label}
                          </Typography>
                        </Link>
                      </div>
                    )
                  }
                })}
              </nav>
              </div>

              {/* Hamburger Menu - mobile/tablet only */}
              <div className="lg:hidden">
                <HamburgerMenu
                  isOpen={isMobileMenuOpen}
                  onClick={toggleMobileMenu}
                  lineColor={theme.textColor.includes('white') ? 'bg-white' : 'bg-civo-black'}
                />
              </div>

              {/* CTA Buttons - desktop only */}
              <div className="hidden lg:flex lg:flex-col xl:flex-row lg:gap-2.5 xl:gap-4">
                {data.ctaButtons?.sales?.enabled && (
                  <NavCTA
                    variant="outline"
                    navigationBackground={navigationBackground}
                    href={resolveHref(data.ctaButtons.sales.salesLink, '/')}
                    newTab={data.ctaButtons.sales.salesLink?.newTab ?? undefined}
                    className="hidden xl:inline-flex"
                  >
                    {data.ctaButtons.sales.salesLink?.label}
                  </NavCTA>
                )}
                {data.ctaButtons?.signup?.enabled && (
                  <NavCTA
                    variant="filled"
                    navigationBackground={navigationBackground}
                    href={resolveHref(data.ctaButtons.signup.signupLink, '/')}
                    newTab={data.ctaButtons.signup.signupLink?.newTab ?? undefined}
                  >
                    {data.ctaButtons.signup.signupLink?.label}
                  </NavCTA>
                )}
              </div>
            </div>

            {/* Horizontal rule under navigation */}
            <div className={`border-b ${theme.textColor.includes('white') ? 'border-white' : 'border-black'}`}>
            </div>
          </div>
        </Navbar>

        {/* Desktop Mega Menu Panel */}
        {openMegaMenu && (
          <div
            data-mega-menu
            className={`hidden lg:block absolute left-0 right-0 ${theme.mainNav} shadow-lg z-50`}
          >
            <div className="max-w-[1440px] mx-auto px-7 md:px-9 xl:px-16 py-8">
              {(data.primaryNavigation || []).map((navItem, index) => {
                const menuId = navItem.id || `nav-${index}`
                if (openMegaMenu === menuId && navItem.megaMenu?.enabled && navItem.megaMenu.items) {
                  return (
                    <div key={menuId} className="flex">
                      {/* Left: Illustration - width matches logo (88px) + separator margin (32px) */}
                      <div className="lg:w-[120px] flex items-center justify-center pr-5">
                        <Image
                          src={`/assets-rebrand/nav-illustrations/${navItem.megaMenu.illustration || 'globe'}.svg`}
                          alt={navItem.megaMenu.dropDownLabel || navItem.navigationLink?.label || 'Navigation illustration'}
                          width={120}
                          height={120}
                          className="w-full h-full object-contain"
                        />
                      </div>

                      {/* Middle: Vertical separator */}
                      <div className={`w-px ${theme.textColor.includes('white') ? 'bg-white' : 'bg-black'}`}></div>

                      {/* Right: Menu items */}
                      {(() => {
                        const layout = navItem.megaMenu.layout || 'standard'
                        const useColumns = layout === 'resources'
                        const linkClass = useColumns ? 'mega-nav-link-columns' : 'mega-nav-link-single'
                        const borderColor = theme.textColor.includes('white') ? 'border-white' : 'border-black'
                        const activeColor = getActiveColor()

                        return (
                          <div className={`flex flex-1 ${useColumns ? 'flex-wrap content-start gap-6' : 'flex-col'} pl-5`}>
                            {navItem.megaMenu.items.map((item, itemIndex) => {
                              const itemHref = resolveHref(item.itemLink, '/')
                              const isItemActive = pathname === itemHref

                              return (
                                <Link
                                  key={item.id || itemIndex}
                                  href={itemHref}
                                  target={item.itemLink?.newTab ? '_blank' : undefined}
                                  rel={item.itemLink?.newTab ? 'noopener noreferrer' : undefined}
                                  className={`group ${useColumns ? 'w-[calc(33.33%-1rem)]' : 'w-full'} border-b ${borderColor} py-4`}
                                >
                                  <div
                                    className={`${linkClass} ${isItemActive ? activeColor : theme.textColor} hover-theme-color flex flex-1 transition-all duration-300 ease-in-out group-hover:translate-x-6 group-hover:opacity-65`}
                                    style={{ '--hover-color': getHoverColorValue() } as React.CSSProperties}
                                  >
                                    {item.itemLink?.label}
                                  </div>
                                </Link>
                              )
                            })}
                          </div>
                        )
                      })()}
                    </div>
                  )
                }
                return null
              })}
            </div>
          </div>
        )}
      </header>

      {/* Logo Context Menu */}
      {logoContextMenuOpen && logoRef.current && (
        <div
          ref={logoContextMenuRef}
          className="fixed z-[100] bg-civo-black rounded-lg shadow-lg min-w-[180px] animate-in fade-in zoom-in-95 duration-150"
          style={{
            left: logoRef.current.getBoundingClientRect().left,
            top: logoRef.current.getBoundingClientRect().bottom + 8,
          }}
        >
          <a
            href="https://www.civo.com/brand-assets/civo-logo-set.zip"
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-civo-lime hover:bg-white/10 transition-colors"
            onClick={() => setLogoContextMenuOpen(false)}
          >
            <Image
              src="assets-rebrand/favicons/favicon.svg"
              alt=""
              width={16}
              height={16}
            />
            Download Logos
          </a>
        </div>
      )}

      {/* Mobile Navigation Overlay */}
      {isMobileMenuOpen && (
        <div className={`fixed inset-x-0 top-[77px] md:top-[137px] bottom-0 z-50 lg:hidden ${theme.mainNav} flex flex-col`}>
          {/* Navigation Content */}
          <nav className="flex-1 overflow-y-auto px-7 pb-8">
            {/* Primary Navigation Section */}
            <div className="flex flex-col">
              {(data.primaryNavigation || []).map((navItem, index) => {
                const sectionKey = `primary-${index}`
                const isExpanded = expandedSections[sectionKey]
                const hasSubItems = navItem.megaMenu?.enabled && navItem.megaMenu.items

                return (
                  <div key={index}>
                    {hasSubItems ? (
                      // Items with sub-navigation (expandable)
                      <>
                        <button
                          onClick={() => toggleSection(sectionKey)}
                          className="flex items-center justify-between py-3 w-full text-left"
                          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${navItem.megaMenu?.dropDownLabel || navItem.navigationLink?.label}`}
                          aria-expanded={isExpanded}
                        >
                          <span className={`mobile-nav-item ${theme.textColor}`}>
                            {navItem.megaMenu?.dropDownLabel || navItem.navigationLink?.label}
                          </span>
                          <div className={`flex items-center justify-center ${theme.textColor}`}>
                            {isExpanded ? (
                              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <line y1="4.88477" x2="10" y2="4.88477" stroke="currentColor"/>
                              </svg>
                            ) : (
                              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <line x1="5.11523" y1="0" x2="5.11523" y2="10" stroke="currentColor"/>
                                <line y1="4.88477" x2="10" y2="4.88477" stroke="currentColor"/>
                              </svg>
                            )}
                          </div>
                        </button>

                        {/* Sub-items */}
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            isExpanded ? 'max-h-[1000px]' : 'max-h-0'
                          }`}
                        >
                          <div className="pl-6 pb-1">
                            {navItem.megaMenu?.items?.map((subItem, subIndex) => (
                              <Link
                                key={subIndex}
                                href={resolveHref(subItem.itemLink, '/')}
                                target={subItem.itemLink?.newTab ? '_blank' : undefined}
                                rel={subItem.itemLink?.newTab ? 'noopener noreferrer' : undefined}
                                className={`block mobile-nav-subitem ${theme.textColor} hover:underline mb-2`}
                              >
                                {subItem.itemLink?.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      </>
                    ) : (
                      // Items without sub-navigation (direct links)
                      <div className="py-3">
                        <Link
                          href={resolveHref(navItem.navigationLink, '/')}
                          className={`mobile-nav-item ${theme.textColor}`}
                        >
                          {navItem.navigationLink?.label}
                        </Link>
                      </div>
                    )}

                    <div className={`h-px w-full ${theme.textColor.includes('white') ? 'bg-white' : 'bg-black'}`}></div>
                  </div>
                )
              })}
            </div>

            {/* Secondary Navigation Section */}
            <div className="flex flex-col mb-6">
              {(data.topSubnav?.quickLinks || []).map((item, index) => {
                return (
                  <div key={index}>
                    <div className="py-3">
                      <Link
                        href={resolveHref(item.link, '/')}
                        target={item.link?.newTab ? '_blank' : undefined}
                        rel={item.link?.newTab ? 'noopener noreferrer' : undefined}
                        className={`body-copy-small ${theme.textColor}`}
                      >
                        {item.link?.label}
                      </Link>
                    </div>
                    <div className={`h-px w-full ${theme.textColor.includes('white') ? 'bg-white' : 'bg-black'}`}></div>
                  </div>
                )
              })}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col gap-3">
              {data.ctaButtons?.signup?.enabled && (
                <Link
                  href={resolveHref(data.ctaButtons.signup.signupLink, '/')}
                  className={`${navigationBackground === 'black' ? 'bg-civo-lime' : 'bg-black'} rounded-full flex items-center justify-between pl-6 pr-1 py-[3px]`}
                >
                  <span className={`font-semibold ${navigationBackground === 'black' ? 'text-black' : 'text-white'} text-base tracking-[-0.112px]`}>
                    {data.ctaButtons.signup.signupLink?.label}
                  </span>
                  <div className={`w-10 h-10 ${navigationBackground === 'black' ? 'bg-black' : 'bg-civo-lime'} rounded-full flex items-center justify-center`}>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4.6665 7.99998H10.4998M10.4998 7.99998L7.58317 5.08331M10.4998 7.99998L7.58317 10.9166" stroke={navigationBackground === 'black' ? '#00FF6F' : 'black'} strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                </Link>
              )}
              <Link
                href={data.topSubnav?.loginUrl || '/login'}
                className={`${navigationBackground === 'black' ? 'bg-transparent border border-white' : navigationBackground === 'lime' ? 'bg-transparent border border-black' : 'bg-black'} rounded-full flex items-center justify-between pl-6 pr-1 py-[3px]`}
              >
                <span className={`font-semibold ${navigationBackground === 'black' ? 'text-white' : navigationBackground === 'lime' ? 'text-black' : 'text-white'} text-base tracking-[-0.112px]`}>
                  Log in
                </span>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${navigationBackground === 'lime' ? 'bg-black' : 'bg-civo-lime'}`}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4.6665 7.99998H10.4998M10.4998 7.99998L7.58317 5.08331M10.4998 7.99998L7.58317 10.9166" stroke={navigationBackground === 'lime' ? '#00FF6F' : 'black'} strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </>
  )
}